import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

type Suite = {
  number: number;
  member_record_id: string | null;
  source: string;
  status: string;
  reserved_at: string | null;
  notes: string | null;
};

type MemberLite = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
};

export default function SuiteRegistry() {
  const [suites, setSuites] = useState<Suite[]>([]);
  const [members, setMembers] = useState<Record<string, MemberLite>>({});
  const [loading, setLoading] = useState(true);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveNumber, setReserveNumber] = useState("");
  const [reserveEmail, setReserveEmail] = useState("");
  const [reserveNote, setReserveNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<Suite | null>(null);
  const [releaseConfirm, setReleaseConfirm] = useState("");
  const [releaseNote, setReleaseNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("suite_numbers")
      .select("*")
      .order("number", { ascending: true })
      .limit(1000);

    if (error) {
      toast.error(error.message);
      setSuites([]);
      setLoading(false);
      return;
    }

    const list = (data || []) as Suite[];
    setSuites(list);

    const ids = Array.from(
      new Set(list.map((s) => s.member_record_id).filter(Boolean) as string[]),
    );
    if (ids.length) {
      const { data: mem } = await (supabase as any)
        .from("member_records")
        .select("id, first_name, last_name, email")
        .in("id", ids);
      const map: Record<string, MemberLite> = {};
      for (const m of (mem || []) as MemberLite[]) map[m.id] = m;
      setMembers(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const nextAuto = useMemo(() => {
    const max = suites.reduce((acc, s) => Math.max(acc, s.number), 0);
    return max + 1;
  }, [suites]);

  const reserveManually = async () => {
    const number = Number(reserveNumber);
    if (!number || number < 1) {
      toast.error("Enter a suite number.");
      return;
    }
    setBusy(true);

    const { data: memberRows } = await (supabase as any)
      .from("member_records")
      .select("id")
      .eq("email", reserveEmail.trim().toLowerCase())
      .limit(1);

    const memberId = memberRows?.[0]?.id;
    if (!memberId) {
      setBusy(false);
      toast.error("We could not find a member with that email.");
      return;
    }

    const { error } = await (supabase as any).rpc("assign_suite", {
      p_member_record_id: memberId,
      p_number: number,
    });

    if (!error && reserveNote.trim()) {
      await (supabase as any)
        .from("suite_numbers")
        .update({ notes: reserveNote.trim() })
        .eq("number", number);
    }

    setBusy(false);

    if (error) {
      toast.error(
        error.message?.includes("duplicate")
          ? `Suite ${number} is already taken. Pick another number.`
          : error.message || "That did not go through.",
      );
      return;
    }

    toast.success(`Suite ${number} reserved.`);
    setReserveOpen(false);
    setReserveNumber("");
    setReserveEmail("");
    setReserveNote("");
    void load();
  };

  const releaseSuite = async () => {
    if (!releaseTarget) return;
    setBusy(true);
    const { error } = await (supabase as any).rpc("release_suite", {
      p_number: releaseTarget.number,
      p_note: releaseNote.trim(),
    });
    setBusy(false);
    if (error) {
      toast.error(error.message || "That did not go through.");
      return;
    }
    toast.success(`Suite ${releaseTarget.number} released.`);
    setReleaseTarget(null);
    setReleaseConfirm("");
    setReleaseNote("");
    void load();
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Suite numbers</h1>
          <p className="text-sm text-muted-foreground">
            Every suite in the registry, who holds it, and where it came from.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="min-h-11">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={() => setReserveOpen(true)} className="min-h-11">
            Reserve manually
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardDescription>Next automatic number</CardDescription>
          <CardTitle className="text-4xl">{nextAuto}</CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Registry ({suites.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Suite</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reserved</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suites.map((s) => {
                  const m = s.member_record_id ? members[s.member_record_id] : null;
                  return (
                    <TableRow key={s.number}>
                      <TableCell className="font-medium">{s.number}</TableCell>
                      <TableCell>
                        {m ? (
                          <div>
                            <div>{`${m.first_name ?? ""} ${m.last_name ?? ""}`.trim()}</div>
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Open</span>
                        )}
                      </TableCell>
                      <TableCell className="capitalize">{s.source}</TableCell>
                      <TableCell>
                        <Badge variant={s.status === "released" ? "secondary" : "default"}>
                          {s.status === "released"
                            ? "Released"
                            : s.status === "reserved"
                              ? "Reserved"
                              : "Active"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.reserved_at ? new Date(s.reserved_at).toLocaleDateString() : ""}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.status !== "released" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setReleaseTarget(s);
                              setReleaseConfirm("");
                              setReleaseNote("");
                            }}
                          >
                            Release
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={reserveOpen} onOpenChange={setReserveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reserve a suite by hand</DialogTitle>
            <DialogDescription>
              Use this when a member needs a specific number. If it is taken, we will say so.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="suite-number">Suite number</Label>
              <Input
                id="suite-number"
                type="number"
                value={reserveNumber}
                onChange={(e) => setReserveNumber(e.target.value)}
                placeholder={String(nextAuto)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="suite-email">Member email</Label>
              <Input
                id="suite-email"
                type="email"
                value={reserveEmail}
                onChange={(e) => setReserveEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="suite-note">Note (optional)</Label>
              <Textarea
                id="suite-note"
                value={reserveNote}
                onChange={(e) => setReserveNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReserveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={reserveManually} disabled={busy}>
              Reserve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(releaseTarget)} onOpenChange={(o) => !o && setReleaseTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release suite {releaseTarget?.number}</DialogTitle>
            <DialogDescription>
              This clears the suite from the member and puts it out of use. Type RELEASE to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="release-confirm">Type RELEASE</Label>
              <Input
                id="release-confirm"
                value={releaseConfirm}
                onChange={(e) => setReleaseConfirm(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="release-note">Why are you releasing it?</Label>
              <Textarea
                id="release-note"
                value={releaseNote}
                onChange={(e) => setReleaseNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReleaseTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={releaseSuite}
              disabled={busy || releaseConfirm !== "RELEASE" || !releaseNote.trim()}
            >
              Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
